import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { todosAPI } from '../api/endpoints';
import type { TodoCounts } from '../types';

const EMPTY_COUNTS: TodoCounts = {
  overdue: 0,
  today: 0,
  later: 0,
  by_status: {},
  by_type: {},
  total: 0,
};

/**
 * Unfiltered todo tallies, shared by the navbar badge and the todo page (its header summary
 * and the counts in the Type / Status filter dropdowns), so the two can never disagree.
 *
 * This replaced walking every page of `GET /todos` just to count rows: the badge needs two
 * numbers, and with the page paginated server-side nothing else wants the whole collection.
 * `overdue`/`today` are cut in the user's own timezone server-side — see `TodoService.counts`.
 */
export function useTodoCounts() {
  return useQuery({
    queryKey: ['todos', 'counts'],
    queryFn: async () => (await todosAPI.counts()).data,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/** `useTodoCounts` with zeroes standing in while the first response is in flight. */
export function useTodoCountsOrZero(): TodoCounts {
  const { data } = useTodoCounts();
  return data ?? EMPTY_COUNTS;
}

/**
 * Refresh every todo view after something changed todos: the venue overlay's list
 * (`['venue-todos', venueId]`), the navbar badge and the Todo page (`['todos', ...]`).
 *
 * Sending an email is one of those writes even though the user never touched a todo — the server
 * marks the contact's still-pending todos done and writes the follow-up (docs/EMAIL_SEND.md) —
 * which is why this lives next to the counts rather than inside `VenueTodos`. Without it the
 * overlay keeps showing the todos it loaded, for the five minutes of the default `staleTime` and
 * for as long as the panel stays mounted.
 *
 * The venue key is invalidated by prefix: the cache can hold the lists of several venues paged
 * through in the overlay, and a send from a contact resolves its venue server-side anyway.
 */
export function useInvalidateTodos() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['venue-todos'] });
    queryClient.invalidateQueries({ queryKey: ['todos'] });
  }, [queryClient]);
}
